import type {
  EvaluationEvidence,
  EvaluationEvidenceSnippet,
  EvaluationEvidenceSpan,
} from '@compass/client/types/evaluation';

// Validates an extracted timestamp body like `4:01`.
const EVIDENCE_TIMESTAMP_TIME_PATTERN = /^(\d+):([0-5]\d)$/;
// Scans full evidence text for inline timestamp candidates like `@4:01`.
const EVIDENCE_TIMESTAMP_TOKEN_PATTERN = /@(\d+):([0-5]\d)/g;
// Blocks partial matches in unsupported longer forms like `@4:01-4:18`.
const INVALID_TIMESTAMP_SUFFIX_PATTERN = /[A-Za-z0-9_:\-–]/;
// Detects meaningful text like ` and DOB ` so separated timestamps do not pair.
const STRONG_TEXT_PATTERN = /[A-Za-z0-9]/;

export interface ParsedEvidenceTimestampToken extends EvaluationEvidenceSpan {
  start_seconds: number;
  text: string;
}

export type EvidenceComposerTextPart =
  | {
      text: string;
      type: 'text';
    }
  | ({
      type: 'timestamp';
    } & ParsedEvidenceTimestampToken);

const parseEvidenceTimestampToSeconds = (value: string): number | null => {
  const match = value.match(EVIDENCE_TIMESTAMP_TIME_PATTERN);
  if (!match) {
    return null;
  }

  const minutes = Number.parseInt(match[1] || '', 10);
  const seconds = Number.parseInt(match[2] || '', 10);
  if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) {
    return null;
  }

  return minutes * 60 + seconds;
};

const hasTokenBoundaryBefore = (value: string, matchIndex: number) => {
  if (matchIndex <= 0) {
    return true;
  }

  // Blocks matching inside words or emails like `email@4:01`.
  return !/[A-Za-z0-9_]/.test(value[matchIndex - 1] || '');
};

const hasTokenBoundaryAfter = (value: string, matchEnd: number) => {
  if (matchEnd >= value.length) {
    return true;
  }

  return !INVALID_TIMESTAMP_SUFFIX_PATTERN.test(value[matchEnd] || '');
};

const hasStrongText = (value: string) => STRONG_TEXT_PATTERN.test(value);

export const formatEvidenceTimestampToken = (seconds: number) => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;
  return `@${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
};

export const appendEvidenceTimestampToken = (value: string, seconds: number) => {
  const token = formatEvidenceTimestampToken(seconds);
  if (!value.trim()) {
    return token;
  }

  // Keeps transcript clicks on the next line when evidence ends like `text\n`.
  if (/\n\s*$/.test(value)) {
    // Removes trailing spaces before appending on a new line, like `text  \n`.
    return `${value.replace(/[ \t]+$/g, '')}${token}`;
  }

  return `${value.trimEnd()} ${token}`;
};

export const parseEvidenceTimestampToken = (text: string): ParsedEvidenceTimestampToken | null => {
  if (!text.startsWith('@')) {
    return null;
  }

  const startSeconds = parseEvidenceTimestampToSeconds(text.slice(1));
  if (startSeconds === null) {
    return null;
  }

  return {
    start_seconds: startSeconds,
    text,
  };
};

export const tokenizeEvidenceComposerText = (value: string): EvidenceComposerTextPart[] => {
  if (!value) {
    return [];
  }

  const parts: EvidenceComposerTextPart[] = [];
  let lastIndex = 0;

  for (const match of value.matchAll(EVIDENCE_TIMESTAMP_TOKEN_PATTERN)) {
    const matchText = match[0];
    const matchIndex = match.index ?? 0;
    const matchEnd = matchIndex + matchText.length;

    if (!hasTokenBoundaryBefore(value, matchIndex) || !hasTokenBoundaryAfter(value, matchEnd)) {
      continue;
    }

    const parsedToken = parseEvidenceTimestampToken(matchText);
    if (!parsedToken) {
      continue;
    }

    if (matchIndex > lastIndex) {
      parts.push({
        text: value.slice(lastIndex, matchIndex),
        type: 'text',
      });
    }

    parts.push({
      ...parsedToken,
      type: 'timestamp',
    });
    lastIndex = matchEnd;
  }

  if (lastIndex < value.length) {
    parts.push({
      text: value.slice(lastIndex),
      type: 'text',
    });
  }

  if (parts.length === 0) {
    return [
      {
        text: value,
        type: 'text',
      },
    ];
  }

  return parts;
};

export const parseEvidenceTextToSnippets = (value: string): EvaluationEvidenceSnippet[] => {
  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }

  const parts = tokenizeEvidenceComposerText(trimmed);
  const spans: EvaluationEvidenceSpan[] = [];
  let pendingStartSeconds: number | null = null;

  const flushPendingStart = () => {
    if (pendingStartSeconds === null) {
      return;
    }

    spans.push({
      start_seconds: pendingStartSeconds,
    });
    pendingStartSeconds = null;
  };

  parts.forEach(part => {
    if (part.type === 'text') {
      if (hasStrongText(part.text)) {
        flushPendingStart();
      }
      return;
    }

    if (pendingStartSeconds === null) {
      pendingStartSeconds = part.start_seconds;
      return;
    }

    spans.push({
      end_seconds: part.start_seconds,
      start_seconds: pendingStartSeconds,
    });
    pendingStartSeconds = null;
  });

  flushPendingStart();

  return [
    {
      spans,
      text: trimmed,
    },
  ];
};

export const buildStructuredEvidenceFromComposerText = (
  value: string,
  assetId?: string,
): EvaluationEvidence | undefined => {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  return {
    asset_id: assetId,
    snippet: trimmed,
    snippets: parseEvidenceTextToSnippets(trimmed),
  };
};
